import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./MyConfigs.module.css";
import {
  useLocalStorage,
  copyToClipboard,
  pushToDataLayer,
} from "../../helpers/helpers";
import keyIcon from "../../img/liebherr-key-icon.svg";
import PropTypes from "prop-types";
import MainNavigation from "../../components/main-navigation/MainNavigation";
import Sidebar from "../../components/sidebar/Sidebar";
import useMutationObserver from "../../hooks/useMutationObserver";
import {
  PatternlibButton,
  PatternlibCheckbox,
  PatternlibAccordion,
  PatternlibAccordionItem,
  PatternlibIcon,
  PatternlibToast,
  PatternlibMainNavigationItem,
  PatternlibCard,
  PatternlibTooltip,
} from "@liebherr/patternlib-react";

const Compare = ({ profile, isDemoUser }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [values, setValues] = useState([]);
  const [selected, setSelected] = useState([]);
  const [items, setItems] = useLocalStorage("saved", []);

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  const [selectedForComparison, setSelectedForComparison] = useLocalStorage(
    "compare",
    []
  );

  // the useRef Hook allows to persist data between renders
  const toastRef = useRef();

  // on first render: clear the session Storage with configuration settings to make sure,
  // that is doesn't get in the way with the saved config data
  useEffect(() => {
    sessionStorage.clear();
  }, []);

  const configAddGA = () => {
    const eventName = "configuration_add";
    const pagePath = location.pathname;
    const pageType = "my_configurations";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    let props = { pagePath, pageType, language };

    pushToDataLayer(eventName, props);
  };

  const configOpenGA = () => {
    const eventName = "configuration_open";
    const pagePath = location.pathname;
    const pageType = "my_configurations";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    let props = { pagePath, pageType, language };

    pushToDataLayer(eventName, props);
  };

  const offerRequestStartGA = () => {
    const eventName = "offer_request_start";
    const pagePath = location.pathname;
    const pageType = "my_configurations";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    let props = { pagePath, pageType, language };

    pushToDataLayer(eventName, props);
  };

  const toStart = () => () => {
    configAddGA();
    navigate("/");
  };

  const navigate = useNavigate();
  const selectionLimit = 3;

  const searchForPart = (type, key) => {
    configOpenGA();
    switch (type) {
      case "S26":
        navigate("/series260", { state: { key: key } });
        break;
      case "S38":
        navigate("/series380", { state: { key: key } });
        break;
      case "ISO":
        navigate("/iso6022", { state: { key: key } });
        break;
      default:
        navigate("/series260", { state: { key: key } });
    }
  };

  const requestOffer = (type, key) => {
    offerRequestStartGA();
    switch (type) {
      case "S26":
        navigate("/summary/series260", { state: { key: key, isOpen: true } });
        break;
      case "S38":
        navigate("/summary/series380", { state: { key: key, isOpen: true } });
        break;
      case "ISO":
        navigate("/summary/iso6022", { state: { key: key, isOpen: true } });
        break;
      default:
        navigate("/summary/series260", { state: { key: key, isOpen: true } });
    }
  };

  const getPlaceholerImage = (key) => {
    switch (key) {
      case "S26":
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/series260/series260_asmtab.png&color=transparent&depth=32";
      case "S38":
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/series380/series380_asmtab.png&color=transparent&depth=32";
      case "ISO":
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/iso6022/iso6022_asmtab.png&color=transparent&depth=32";
      default:
        return "https://webapi.partcommunity.com/FileService/CatalogImage?PreviewPath=liebherr_components/series260/series260_asmtab.png&color=transparent&depth=32";
    }
  };

  const removePart = (id) => {
    removeFromSelection(id);

    const arr = [...values];
    const index = arr.findIndex((element) => element["id"] === id);

    if (index > -1) {
      arr.splice(index, 1); // 2nd parameter means remove one item only
      setValues(arr);
      setItems(arr);
    }
  };

  const addToSelection = (id) => {
    const arr = [...selected];
    arr.push(id);
    setSelected(arr);
    setSelectedForComparison(arr);
  };

  const removeFromSelection = (id) => {
    const arr = [...selected];
    const index = arr.indexOf(id);
    if (index > -1) {
      arr.splice(index, 1); // 2nd parameter means remove one item only
      setSelected(arr);
      setSelectedForComparison(arr);
    }
  };

  const copyKeyToClipboard = (key) => async () => {
    const toastItem = await copyToClipboard(key, t("KeyButton.message"));
    if (toastItem !== null) {
      const toast = toastRef.current;
      toast.createToastItem({
        ...toastItem,
      });
    }
  };

  const TableCheckbox = ({ index, id }) => {
    const onChangeListener = (event) => {
      console.log(event);
      if (event.detail.value) {
        addToSelection(id);
      } else {
        removeFromSelection(id);
      }
    };

    const checked = selected.includes(id) ? true : false;
    const disabled = selected.length >= selectionLimit;

    return (
      <PatternlibCheckbox
        checked={checked}
        name={index}
        disabled={disabled && !checked}
        onLhChange={onChangeListener}
      ></PatternlibCheckbox>
    );
  };

  TableCheckbox.propTypes = {
    index: PropTypes.number,
    id: PropTypes.string,
  };

  const getListItems = () => {
    const arr = [...values];
    arr.reverse();
    const items = arr.map((item, index) => {
      return (
        <tr key={index} className={styles.detailsContainer}>
          <td className={`${styles.td} ${styles.checkbox}`}>
            <TableCheckbox
              className={styles.checkbox}
              index={index}
              id={item["id"]}
            ></TableCheckbox>
          </td>
          <td className={styles.td}>
            <img
              className={styles.preview}
              src={
                item["image"] ? item["image"] : getPlaceholerImage(item["type"])
              }
              alt="img"
            ></img>
          </td>
          <td className={`${styles.td} ${styles.infoText}`}>
            <h3>{item["customName"]}</h3>
            <div>
              <div className={styles.keyTitle}>
                <img src={keyIcon} width="32px"></img>
                <h4>{t("infotexts.general.key.title")}</h4>
              </div>
              <div className={styles.key}>
                <span>{item["part"]}</span>
                <PatternlibButton
                  size="small"
                  label={t("KeyButton.copy")}
                  onClick={copyKeyToClipboard(item["part"])}
                ></PatternlibButton>
              </div>
            </div>
            {item["customNotes"] && (
              <div className={styles.customNotes}>
                <span>{item["customNotes"]}</span>
              </div>
            )}
          </td>
          <td className={styles.td}>
            <div className={styles.buttons}>
              <PatternlibButton
                width="auto"
                display="flex"
                type="secondary"
                label={t("myconfigs.open")}
                onClick={() => searchForPart(item["type"], item["part"])}
                icon-position="right"
              >
                <PatternlibIcon
                  icon-name="arrow-right"
                  size="24px"
                  slot="icon"
                />
              </PatternlibButton>
              <PatternlibButton
                width="auto"
                display="flex"
                type="secondary"
                label={t("SendButton.requestOfferShort")}
                onClick={() => requestOffer(item["type"], item["part"])}
                icon-position="right"
              >
                <PatternlibIcon
                  icon-name="arrow-right"
                  size="24px"
                  slot="icon"
                />
              </PatternlibButton>
              <PatternlibButton
                width="auto"
                display="flex"
                type="secondary"
                label={t("myconfigs.remove")}
                onClick={() => removePart(item["id"])}
              >
                <PatternlibIcon icon-name="bin" size="24px" slot="icon" />
              </PatternlibButton>
            </div>
          </td>
          <td className={styles.td}>
            <PatternlibAccordion>
              <PatternlibAccordionItem label={t("myconfigs.data")}>
                {item["datalist"]}
              </PatternlibAccordionItem>
            </PatternlibAccordion>
          </td>
        </tr>
      );
    });

    return <tbody>{items}</tbody>;
  };

  const getListItemsForMobile = () => {
    const arr = [...values];
    arr.reverse();
    const items = arr.map((item, index) => {
      const checked = selected.includes(item["id"]);
      return (
        <div className={checked && styles.checked} key={item["id"]}>
          <PatternlibCard height="100%" no-states no-border={checked}>
            <div className={styles.detailsContainer} slot="card-content">
              <TableCheckbox
                className={styles.checkbox}
                index={index}
                id={item["id"]}
              ></TableCheckbox>
              <img
                className={styles.preview}
                src={
                  item["image"]
                    ? item["image"]
                    : getPlaceholerImage(item["type"])
                }
              ></img>
              <div className={styles.titleWrapper}>
                <h3 className={styles.title} title={item["customName"]}>
                  {item["customName"]}
                </h3>
                <div className={styles.key}>
                  <span>{item["part"]}</span>
                  <PatternlibButton
                    size="small"
                    label={t("KeyButton.copy")}
                    onClick={copyKeyToClipboard(item["part"])}
                  ></PatternlibButton>
                </div>
                <div className={styles.customNotes}>
                  <span title={item["customNotes"]}>
                    {item["customNotes"]
                      ? item["customNotes"]
                      : t("compare.noNotes")}
                  </span>
                </div>
              </div>
              <div className={styles.buttons}>
                <PatternlibButton
                  width="auto"
                  display="flex"
                  type="secondary"
                  label={t("myconfigs.open")}
                  onClick={() => searchForPart(item["type"], item["part"])}
                  icon-position="right"
                >
                  <PatternlibIcon
                    icon-name="arrow-right"
                    size="24px"
                    slot="icon"
                  />
                </PatternlibButton>
                <PatternlibButton
                  width="auto"
                  display="flex"
                  type="secondary"
                  label={t("SendButton.requestOfferShort")}
                  onClick={() => requestOffer(item["type"], item["part"])}
                  icon-position="right"
                >
                  <PatternlibIcon
                    icon-name="arrow-right"
                    size="24px"
                    slot="icon"
                  />
                </PatternlibButton>
                <PatternlibButton
                  width="auto"
                  display="flex"
                  type="secondary"
                  label={t("myconfigs.remove")}
                  onClick={() => removePart(item["id"])}
                >
                  <PatternlibIcon icon-name="bin" size="24px" slot="icon" />
                </PatternlibButton>
              </div>
              <div className={styles.datalist}>
                <PatternlibAccordion>
                  <PatternlibAccordionItem label={t("myconfigs.data")}>
                    {item["datalist"]}
                  </PatternlibAccordionItem>
                </PatternlibAccordion>
              </div>
            </div>
          </PatternlibCard>
        </div>
      );
    });

    return <div className={styles.wrapper}>{items}</div>;
  };

  const toCompare = () => {
    navigate("/compare");
  };

  const CompareButton = () => {
    return (
      <div
        className={styles.compareButton}
        style={{
          pointerEvents: selected.length == 0 ? "none" : "auto",
        }}
      >
        <PatternlibButton
          label-text-align="left"
          icon-position="right"
          width="256px"
          disabled={selected.length == 0}
          label={
            t("myconfigs.goToComparison") +
            " " +
            "(" +
            selected.length +
            "/" +
            selectionLimit +
            ")"
          }
          onClick={toCompare}
        >
          <PatternlibIcon slot="icon" icon-name="font-arrow"></PatternlibIcon>
        </PatternlibButton>
      </div>
    );
  };
  CompareButton.propTypes = {
    selected: PropTypes.array,
  };

  // Copy items and inject datalist to each item. Then update state.
  const injectItemsData = (items) => {
    const itemWithValues = items.map((item) => {
      const datalist = [];
      if (item["data"]) {
        Object.keys(item["data"]).map(function (key) {
          const obj = item["data"][key];
          const excludeKeys = ["POS", "SAEINFO", "P", "fixedD", "fixedSD"];
          if (!excludeKeys.includes(key)) {
            if (
              obj["status"] === "static" ||
              obj["status"] === "" ||
              obj["status"] === "ranged" ||
              obj["status"] === "fixed"
            ) {
              datalist.push(
                <li key={key}>
                  <span className={styles.icons}></span>
                  <b>{obj["desc"]}</b>
                  <br></br>
                  {obj["resolvedVal"] + " " + obj["Unit"]}
                </li>
              );
            }
          }
          item["datalist"] = <ul className={styles.list}>{datalist}</ul>;
        });
      }
      return item;
    });
    setValues(itemWithValues);
  };

  useLayoutEffect(() => {
    // Reset selected items if item does not found in saved items
    let success = false;
    try {
      selectedForComparison.map((id) => {
        const index = items.findIndex((element) => element["id"] === id);
        success = true;

        if (index === -1) {
          success = false;
          throw new Error(t("compare.errorMessage"));
        }
      });
    } catch (error) {
      console.error(error);
      // If error reset the selected items
      setSelectedForComparison([]);
    } finally {
      if (success) {
        injectItemsData(items);
        setSelected(selectedForComparison);
      } else {
        injectItemsData(items);
        setSelected([]);
      }
    }
  }, []);

  const toPage = (tabId) => () => {
    switch (tabId) {
      case 0:
        navigate("/myconfigs");
        break;
      case 1:
        navigate("/contact");
        break;
      default:
        navigate("/");
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.navigation}>
          <MainNavigation
            activeTab={1}
            profile={profile}
            isDemoUser={isDemoUser}
          ></MainNavigation>
        </div>
      </header>
      <div className={styles.body}>
        <div className={styles.sidebar}>
          <div className={styles.sidebarWrapper}>
            {screen === "isMobile" ? (
              <>
                <PatternlibMainNavigationItem is-active onClick={toPage(0)}>
                  {t("Sidebar.myConfigs")}
                </PatternlibMainNavigationItem>
                <PatternlibMainNavigationItem onClick={toPage(1)}>
                  {t("Sidebar.contact")}
                </PatternlibMainNavigationItem>
              </>
            ) : (
              <Sidebar activeTab={0}></Sidebar>
            )}
          </div>
        </div>
        <div className={styles.panel}>
          <div
            className={`${styles.panelContent} ${
              isDemoUser && styles.reducePadding
            }`}
          >
            <h2>{t("myconfigs.myconfigsTitle")}</h2>
            <PatternlibTooltip tooltip-position="center">
              <CompareButton></CompareButton>
              <div slot="content" className={styles.tooltipContent}>
                {t("myconfigs.comparisonInfo")}
              </div>
            </PatternlibTooltip>
            <div className={styles.configs}>
              {values.length > 0 ? (
                screen === "isMobile" || screen === "isTablet" ? (
                  getListItemsForMobile()
                ) : (
                  <table className={styles.table}>
                    <thead className={styles.thead}>
                      <tr>
                        <th className={styles.th}>{t("myconfigs.compare")}</th>
                        <th className={styles.th}>{t("myconfigs.preview")}</th>
                        <th className={styles.th}>{t("myconfigs.id")}</th>
                        <th className={styles.th}>{t("myconfigs.actions")}</th>
                        <th className={styles.th}>{t("myconfigs.details")}</th>
                      </tr>
                    </thead>
                    {getListItems()}
                  </table>
                )
              ) : (
                <p>{t("myconfigs.nothingSaved")}</p>
              )}
              <div className={styles.plusButton}>
                <PatternlibButton width="56px" onClick={toStart()}>
                  <PatternlibIcon icon-name="plus" size="24px" slot="icon" />
                </PatternlibButton>
              </div>
            </div>
            <PatternlibToast
              position="top-right"
              ref={toastRef}
              width={screen === "isMobile" ? "256px" : "408px"}
            ></PatternlibToast>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Compare;
